import { Link } from "@tanstack/react-router";
import { Store } from "lucide-react";
import { useStores } from "@/api/queries";
import type { StoreFilters, StoreRow } from "@/api/types";
import { IdBadge } from "@/components/feedback/id-badge";
import { StatusBadge } from "@/components/feedback/status-badge";
import { OrgLink } from "@/components/navigation/entity-links";
import { AdminTable, type Column } from "@/components/tables/admin-table";
import { CopyIdsButton } from "@/components/tables/copy-ids";
import { type ResourceTableProps, tablePaging } from "@/components/tables/types";
import { formatNumber } from "@/lib/formatters";
import { STORE } from "@/lib/status";

export function StoresTable({ query, onChange, toolbar, hide = [], filtered }: ResourceTableProps<StoreFilters>) {
  const q = useStores(query);
  const columns: Column<StoreRow>[] = [
    {
      id: "store",
      header: "Store",
      sortKey: "name",
      alwaysVisible: true,
      cell: (s) => (
        <span className="flex flex-col">
          <Link to="/stores/$storeId" params={{ storeId: s.id }} className="font-medium hover:underline">
            {s.name}
          </Link>
          <IdBadge id={s.id} className="text-muted-foreground" />
        </span>
      ),
    },
    { id: "organization", header: "Organization", sortKey: "organization", cell: (s) => <OrgLink org={s.organization} /> },
    { id: "brand", header: "Brand", cell: (s) => s.brand ?? "—" },
    { id: "location", header: "Location", cell: (s) => s.location ?? "—" },
    { id: "users", header: "Users", sortKey: "userCount", align: "right", cell: (s) => formatNumber(s.userCount) },
    { id: "buyers", header: "Buyers", align: "right", cell: (s) => formatNumber(s.buyerCount) },
    { id: "active", header: "Active opps", sortKey: "activeOpportunities", align: "right", cell: (s) => formatNumber(s.activeOpportunities) },
    { id: "appointments", header: "Appts (±7d)", align: "right", cell: (s) => formatNumber(s.appointments7d) },
    { id: "purchases", header: "Purchases (30d)", sortKey: "purchases30d", align: "right", cell: (s) => formatNumber(s.purchases30d) },
    { id: "status", header: "Status", cell: (s) => <StatusBadge config={STORE[s.status]} /> },
  ];
  return (
    <AdminTable
      id="stores"
      label="Stores"
      resourceName="stores"
      columns={columns.filter((c) => !hide.includes(c.id))}
      rows={q.data?.items}
      rowKey={(s) => s.id}
      isPending={q.isPending}
      isFetching={q.isFetching}
      error={q.error}
      onRetry={() => void q.refetch()}
      {...tablePaging(query, q.data?.total, onChange)}
      selectable
      bulkActions={(rows) => <CopyIdsButton ids={rows.map((r) => r.id)} />}
      toolbar={toolbar}
      empty={
        filtered
          ? { icon: Store, title: "No stores match", description: "Clear filters or broaden the search." }
          : { icon: Store, title: "No stores", description: "Stores appear once an organization adds a rooftop." }
      }
    />
  );
}
